import React, { useEffect, useState } from 'react';
import { useLocationContext } from '../context/LocationContext';
import { useCrmLabels } from '../lib/crmLabels';
import { getCurrentReviewerLabel } from '../api/authApi';
import {
    appointmentTypeMappingApi,
    appointmentTypeMappingManualApi,
    ReverseMappingConflictError,
    type AppointmentTypeMappingSuggestion,
    type GHLCalendar,
} from '../api/appointmentTypeMappingApi';
import { useMappingRunProgress } from '../hooks/useMappingRunProgress';
import { SegmentedToggle } from '../components/mapping/SegmentedToggle';
import { ConfidenceBar } from '../components/mapping/ConfidenceBar';
import { StatusBadge, ReviewedByCaption } from '../components/mapping/StatusBadge';
import { MappingCoverageBadge, MappingCoverageArrow } from '../components/mapping/MappingCoverageBadge';
import { MappingProgressSpinner } from '../components/mapping/MappingProgressSpinner';
import { ReasoningModal } from '../components/mapping/ReasoningModal';
import { ManualPicker, type PickerItem } from '../components/mapping/ManualPicker';
import { EditMappingModal } from '../components/mapping/EditMappingModal';
import { ReverseMappingConflictDialog } from '../components/mapping/ReverseMappingConflictDialog';
import { Pagination } from '../components/mapping/Pagination';
import { Dropdown } from '../components/mapping/Dropdown';
import { CONFIDENCE_TIER_OPTIONS, confidenceRangeForTier } from '../components/mapping/confidenceTier';
import { Toast } from '../components/shared/Toast';
import { ConfirmDialog } from '../components/shared/ConfirmDialog';

const STATUS_OPTIONS = [
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'engine_mapped', label: 'Engine mapped' },
];

const PAGE_SIZE = 20;

type Mode = 'ai' | 'manual';

// The backend sets subtype fields to the literal string 'N/A' when an appointment type has no real subtype.
const hasSubtype = (subtypeName: string) => !!subtypeName && subtypeName.toUpperCase() !== 'N/A';
const typeLabel = (typeName: string, subtypeName: string) => hasSubtype(subtypeName) ? `${typeName} - ${subtypeName}` : typeName;

export const AppointmentTypeMappingPage: React.FC<{ initialStatusFilter?: string }> = ({ initialStatusFilter }) => {
    const { locationId } = useLocationContext();
    const labels = useCrmLabels();
    const [mode, setMode] = useState<Mode>('ai');
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

    // ---- AI review state ----
    const [suggestions, setSuggestions] = useState<AppointmentTypeMappingSuggestion[]>([]);
    const [total, setTotal] = useState(0);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [showConf, setShowConf] = useState(true);
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState(initialStatusFilter ?? '');
    const [confidenceTier, setConfidenceTier] = useState('');
    const [typeNameFilter, setTypeNameFilter] = useState('');
    const [calendarNameFilter, setCalendarNameFilter] = useState('');
    const [filterOptions, setFilterOptions] = useState<{ type_name: string[]; subtype_name: string[]; calendar_name: string[] }>({ type_name: [], subtype_name: [], calendar_name: [] });
    const [reasoningModal, setReasoningModal] = useState<{ src: string; tgt: string; reasoning: string } | null>(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [triggering, setTriggering] = useState(false);
    const [editSuggestion, setEditSuggestion] = useState<AppointmentTypeMappingSuggestion | null>(null);
    const [editCalendars, setEditCalendars] = useState<GHLCalendar[]>([]);
    const [loadingEditCalendars, setLoadingEditCalendars] = useState(false);

    // ---- Manual mode state ----
    const [calendars, setCalendars] = useState<GHLCalendar[]>([]);
    const [emrCombos, setEmrCombos] = useState<{ type_external_id: string; type_name: string; subtype_external_id: string; subtype_name: string }[]>([]);
    const [emrSource, setEmrSource] = useState('');
    const [loadingManualData, setLoadingManualData] = useState(false);
    const [resourceEhrId, setResourceEhrId] = useState('');
    const [providerEhrId, setProviderEhrId] = useState('');
    const [history, setHistory] = useState<{ src: string; tgt: string }[]>([]);

    // ---- Reverse-mapping conflict dialog (shared by approve / manual map / edit) ----
    const [conflict, setConflict] = useState<{
        calendarName: string;
        conflictingTypeName: string;
        newTypeName: string;
        retry: (choice: 'keep' | 'overwrite') => Promise<void>;
    } | null>(null);
    const [resolvingConflict, setResolvingConflict] = useState(false);

    const { isPolling, runProgress, startPolling } = useMappingRunProgress(
        locationId,
        'appointment_type',
        {
            onTick: () => setRefreshTrigger(t => t + 1),
            onFailed: message => setToast({ message, type: 'error' }),
        },
    );

    // Debounce the search box before it drives a server request.
    useEffect(() => {
        const t = setTimeout(() => setDebouncedQuery(query), 400);
        return () => clearTimeout(t);
    }, [query]);

    // Reset to page 1 whenever a filter/search value changes.
    useEffect(() => {
        setPage(1);
    }, [debouncedQuery, statusFilter, confidenceTier, typeNameFilter, calendarNameFilter]);

    useEffect(() => {
        if (!locationId) { setSuggestions([]); setTotal(0); return; }
        setLoadingSuggestions(true);
        const confRange = confidenceRangeForTier(confidenceTier);
        appointmentTypeMappingApi.getSuggestions(locationId, {
            limit: PAGE_SIZE,
            offset: (page - 1) * PAGE_SIZE,
            search: debouncedQuery || undefined,
            status: statusFilter || undefined,
            minConfidence: confRange.min,
            maxConfidence: confRange.max,
            typeName: typeNameFilter || undefined,
            calendarName: calendarNameFilter || undefined,
        })
            .then(res => { setSuggestions(res.suggestions); setTotal(res.total); })
            .catch(() => { setSuggestions([]); setTotal(0); })
            .finally(() => setLoadingSuggestions(false));
    }, [locationId, refreshTrigger, page, debouncedQuery, statusFilter, confidenceTier, typeNameFilter, calendarNameFilter]);

    useEffect(() => {
        if (!locationId) { setFilterOptions({ type_name: [], subtype_name: [], calendar_name: [] }); return; }
        appointmentTypeMappingApi.getFilterOptions(locationId)
            .then(setFilterOptions)
            .catch(() => setFilterOptions({ type_name: [], subtype_name: [], calendar_name: [] }));
    }, [locationId, refreshTrigger]);

    useEffect(() => {
        if (mode !== 'manual' || !locationId) return;
        setLoadingManualData(true);
        Promise.all([
            appointmentTypeMappingManualApi.getGHLCalendars(locationId),
            appointmentTypeMappingManualApi.getEMRAppointmentTypes(locationId),
        ]).then(([cals, emrResp]) => {
            setCalendars(cals);
            setEmrSource(emrResp.emr_source);
            setEmrCombos(emrResp.combinations.filter(c =>
                !c.type_name?.toUpperCase().includes('[DELETED]') && !c.subtype_name?.toUpperCase().includes('[DELETED]')
            ));
        }).catch(() => setToast({ message: 'Failed to load manual mapping data', type: 'error' }))
            .finally(() => setLoadingManualData(false));
    }, [mode, locationId]);

    const handleClearFilters = () => {
        setQuery(''); setStatusFilter(''); setConfidenceTier('');
        setTypeNameFilter(''); setCalendarNameFilter('');
    };

    // Optimistic reviewer fields — an instant best-guess shown until the next
    // refetch confirms the server's exact display name (it prefers the
    // reviewer's `name` over their username; the JWT only carries the latter).
    const reviewedFields = (status: 'approved' | 'rejected') => ({
        status,
        is_reviewed: true,
        reviewed_by_name: getCurrentReviewerLabel(),
        reviewed_at: new Date().toISOString(),
    });

    const handleApprove = async (id: number) => {
        if (id < 0) return;
        try {
            await appointmentTypeMappingApi.reviewSuggestion(id, { action: 'approve' });
            setSuggestions(prev => prev.map(s => s.id === id ? { ...s, ...reviewedFields('approved') } : s));
        } catch (err) {
            if (err instanceof ReverseMappingConflictError) {
                const s = suggestions.find(x => x.id === id);
                setConflict({
                    calendarName: err.calendarName,
                    conflictingTypeName: err.conflictingTypeName,
                    newTypeName: s ? typeLabel(s.type_name, s.subtype_name) : '',
                    retry: async (choice) => {
                        await appointmentTypeMappingApi.reviewSuggestion(id, { action: 'approve', reverse_mapping_choice: choice });
                        setSuggestions(prev => prev.map(s => s.id === id ? { ...s, ...reviewedFields('approved') } : s));
                    },
                });
                return;
            }
            setToast({ message: err instanceof Error ? err.message : 'Failed to approve mapping', type: 'error' });
        }
    };

    const handleReject = async (id: number) => {
        if (id < 0) return;
        await appointmentTypeMappingApi.reviewSuggestion(id, { action: 'reject' });
        setSuggestions(prev => prev.map(s => s.id === id ? { ...s, ...reviewedFields('rejected') } : s));
    };

    const handleDelete = async (id: number) => {
        if (id < 0) return;
        setDeleting(true);
        try {
            await appointmentTypeMappingApi.deleteSuggestion(id);
            setSuggestions(prev => prev.filter(s => s.id !== id));
            setTotal(t => Math.max(0, t - 1));
            setToast({ message: 'Mapping deleted', type: 'success' });
        } catch (err) {
            setToast({ message: err instanceof Error ? err.message : 'Failed to delete mapping', type: 'error' });
        } finally {
            setDeleting(false);
            setConfirmDeleteId(null);
        }
    };

    const [confirmApproveAll, setConfirmApproveAll] = useState(false);
    const highConfCount = suggestions.filter(s => s.status === 'pending' && s.confidence_score >= 90).length;

    const requestApproveAll = () => {
        if (highConfCount === 0) return;
        setConfirmApproveAll(true);
    };

    const handleApproveAll = async () => {
        const targets = suggestions.filter(s => s.status === 'pending' && s.confidence_score >= 90);
        if (targets.length === 0) return;

        const results = await Promise.allSettled(targets.map(async s => {
            try {
                await appointmentTypeMappingApi.reviewSuggestion(s.id, { action: 'approve' });
            } catch (err) {
                // Bulk approve never blocks on a dialog — a reverse-mapping conflict
                // here just keeps today's behavior (existing reverse mapping untouched).
                if (err instanceof ReverseMappingConflictError) {
                    await appointmentTypeMappingApi.reviewSuggestion(s.id, { action: 'approve', reverse_mapping_choice: 'keep' });
                    return;
                }
                throw err;
            }
        }));

        const approvedIds = new Set(targets.filter((_, i) => results[i].status === 'fulfilled').map(t => t.id));
        const failedCount = results.length - approvedIds.size;

        setSuggestions(prev => prev.map(s => approvedIds.has(s.id) ? { ...s, ...reviewedFields('approved') } : s));

        if (failedCount > 0) {
            setToast({
                message: `Approved ${approvedIds.size} of ${targets.length} high-confidence mapping(s) — ${failedCount} failed`,
                type: 'error',
            });
        } else {
            setToast({ message: `Approved ${approvedIds.size} high-confidence mapping(s)`, type: 'success' });
        }
    };

    const handleRunAi = async () => {
        if (!locationId) return;
        setTriggering(true);
        try {
            await appointmentTypeMappingApi.triggerMapping({ gh_id: locationId });
            setRefreshTrigger(t => t + 1);
            startPolling();
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to trigger mapping';
            setToast({ message: message.startsWith('EMR_NOT_SYNCED:') ? message.replace('EMR_NOT_SYNCED: ', '') : message, type: 'error' });
        } finally {
            setTriggering(false);
        }
    };

    const openEdit = async (s: AppointmentTypeMappingSuggestion) => {
        if (!locationId || s.is_external || s.status === 'engine_mapped' || s.id < 0) return;
        setEditSuggestion(s);
        setLoadingEditCalendars(true);
        try {
            setEditCalendars(await appointmentTypeMappingManualApi.getGHLCalendars(locationId));
        } catch {
            setToast({ message: `Failed to load ${labels.calendars}`, type: 'error' });
        } finally {
            setLoadingEditCalendars(false);
        }
    };

    const handleEditMap = async (ghlItem: { id: string | number; name: string }) => {
        if (!editSuggestion) return;
        const suggestionId = editSuggestion.id;
        try {
            await appointmentTypeMappingManualApi.updateManualMapping(suggestionId, Number(ghlItem.id), ghlItem.name);
            setRefreshTrigger(t => t + 1);
            setToast({ message: 'Mapping updated', type: 'success' });
        } catch (err) {
            if (err instanceof ReverseMappingConflictError) {
                setConflict({
                    calendarName: err.calendarName,
                    conflictingTypeName: err.conflictingTypeName,
                    newTypeName: typeLabel(editSuggestion.type_name, editSuggestion.subtype_name),
                    retry: async (choice) => {
                        await appointmentTypeMappingManualApi.updateManualMapping(suggestionId, Number(ghlItem.id), ghlItem.name, choice);
                        setRefreshTrigger(t => t + 1);
                        setToast({ message: 'Mapping updated', type: 'success' });
                    },
                });
                return;
            }
            throw err;
        }
    };

    const handleManualMap = async (src: PickerItem, tgt: PickerItem) => {
        if (!locationId) return;
        const combo = emrCombos.find(c => `${c.type_external_id}-${c.subtype_external_id}` === src.id);
        const cal = calendars.find(c => c.id === tgt.id);
        if (!combo || !cal) throw new Error('Selected item not found');
        const payload = {
            gh_id: locationId,
            type_external_id: combo.type_external_id,
            type_name: combo.type_name,
            subtype_external_id: combo.subtype_external_id,
            subtype_name: combo.subtype_name,
            calendar_id: cal.id,
            calendar_name: cal.name,
            ...(emrSource === 'cobalt' && { cobalt_resource_ehr_id: resourceEhrId, cobalt_provider_ehr_id: providerEhrId }),
        };
        try {
            await appointmentTypeMappingManualApi.createManualMapping(payload);
            setHistory(prev => [{ src: src.name, tgt: tgt.name }, ...prev]);
            setToast({ message: 'Mapping created', type: 'success' });
        } catch (err) {
            if (err instanceof ReverseMappingConflictError) {
                setConflict({
                    calendarName: err.calendarName,
                    conflictingTypeName: err.conflictingTypeName,
                    newTypeName: typeLabel(combo.type_name, combo.subtype_name),
                    retry: async (choice) => {
                        await appointmentTypeMappingManualApi.createManualMapping({ ...payload, reverse_mapping_choice: choice });
                        setHistory(prev => [{ src: src.name, tgt: tgt.name }, ...prev]);
                        setToast({ message: 'Mapping created', type: 'success' });
                    },
                });
                return;
            }
            throw err;
        }
    };

    const srcItems: PickerItem[] = emrCombos.map(c => ({
        id: `${c.type_external_id}-${c.subtype_external_id}`,
        name: typeLabel(c.type_name, c.subtype_name),
        sub: hasSubtype(c.subtype_name) ? `${c.type_external_id} / ${c.subtype_external_id}` : c.type_external_id,
    }));
    const tgtItems: PickerItem[] = calendars.map(c => ({ id: c.id, name: c.name, sub: c.description || c.calendar_type }));

    const aiGridCols = showConf ? '1.4fr_44px_1.5fr_150px_130px' : '1.4fr_44px_1.5fr_130px';

    return (
        <div className="mapper-ui font-sans text-mapper-text max-w-[1240px] mx-auto px-10 pt-[30px] pb-[70px]">
            {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} bottomOffset={isPolling ? 120 : 24} />}
            <MappingProgressSpinner
                active={isPolling || triggering}
                processed={runProgress?.count ?? null}
                total={runProgress?.total ?? null}
            />
            <ConfirmDialog
                open={confirmApproveAll}
                title="Approve high-confidence matches?"
                message={`This approves ${highConfCount} match${highConfCount === 1 ? '' : 'es'} the AI is highly confident about (90% or higher) and connects them in ${labels.system}. You can still change any of them afterwards.`}
                confirmLabel="Approve all"
                onConfirm={() => { setConfirmApproveAll(false); handleApproveAll(); }}
                onCancel={() => setConfirmApproveAll(false)}
            />

            <div className="flex items-start justify-between gap-5 mb-[22px]">
                <div>
                    <h1 className="text-2xl font-extrabold tracking-[-0.5px] mb-1">Appointment Type Mapping</h1>
                    <p className="text-sm text-mapper-muted m-0 max-w-[560px]">
                        Map each EMR appointment type + subtype to the {labels.calendar} it should book into.
                    </p>
                </div>
                <SegmentedToggle
                    value={mode}
                    onChange={v => setMode(v as Mode)}
                    options={[
                        {
                            value: 'ai', label: 'AI Review', activeColorClass: 'text-mapper-teal-hover',
                            icon: (
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" /><circle cx="12" cy="12" r="3.2" />
                                </svg>
                            ),
                        },
                        {
                            value: 'manual', label: 'Manual', activeColorClass: 'text-mapper-purple',
                            icon: (
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="6" cy="6" r="2.2" /><circle cx="18" cy="18" r="2.2" /><path d="M8.2 6H14a4 4 0 0 1 4 4v5.6" />
                                </svg>
                            ),
                        },
                    ]}
                />
            </div>

            {!locationId ? (
                <div className="bg-mapper-white border border-mapper-border rounded-[15px] p-6 text-[13.5px] text-mapper-muted">
                    Select a location above to view its appointment type mappings.
                </div>
            ) : mode === 'ai' ? (
                <div>
                    <div className="flex items-center gap-3 mb-4 flex-wrap">
                        <div className="relative flex-1 min-w-[220px] max-w-[340px]">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#98A2B0" strokeWidth="2" strokeLinecap="round" className="absolute left-3 top-1/2 -translate-y-1/2">
                                <circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" />
                            </svg>
                            <input
                                value={query}
                                onChange={e => setQuery(e.target.value)}
                                placeholder="Search unmapped items…"
                                className="w-full pl-[34px] pr-3 py-[9px] border border-mapper-border-2 rounded-[10px] text-[13.5px] bg-mapper-white outline-none text-mapper-text"
                            />
                        </div>
                        <div className="flex-1" />
                        <button
                            onClick={() => setShowConf(v => !v)}
                            title="Show or hide the confidence column"
                            className="flex items-center gap-[9px] py-2 pl-[14px] pr-3 rounded-[10px] border border-mapper-border-2 bg-mapper-white text-mapper-body-text text-[13px] font-semibold"
                        >
                            <span>Confidence</span>
                            <span className={`relative w-[34px] h-5 rounded-full transition-colors ${showConf ? 'bg-mapper-teal' : 'bg-mapper-border-2'}`}>
                                <span
                                    className="absolute top-[2px] w-4 h-4 rounded-full bg-white shadow-mapper-knob transition-all"
                                    style={{ left: showConf ? 16 : 2 }}
                                />
                            </span>
                        </button>
                        <button
                            onClick={requestApproveAll}
                            disabled={highConfCount === 0}
                            title={highConfCount === 0 ? 'No high-confidence matches waiting for review' : undefined}
                            className="flex items-center gap-[7px] px-[15px] py-[9px] rounded-[10px] border border-mapper-teal-border bg-mapper-teal-bg text-mapper-teal-hover text-[13px] font-semibold disabled:opacity-50 disabled:cursor-default"
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 6 9 17l-5-5" />
                            </svg>
                            Approve all high-confidence
                        </button>
                        <button
                            onClick={handleRunAi}
                            disabled={triggering || isPolling}
                            className="flex items-center gap-[7px] px-[15px] py-[9px] rounded-[10px] bg-mapper-dark text-white text-[13px] font-semibold disabled:opacity-70"
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" /><circle cx="12" cy="12" r="3.2" />
                            </svg>
                            {triggering || isPolling ? 'Running…' : 'Run AI mapping'}
                        </button>
                    </div>

                    <div className="bg-mapper-white border border-mapper-border rounded-[15px] overflow-hidden">
                        <div
                            className="grid items-center gap-[14px] px-[22px] py-3 border-b border-mapper-border-3 bg-mapper-surface text-[11.5px] font-bold tracking-[0.4px] text-mapper-muted-3"
                            style={{ gridTemplateColumns: aiGridCols.replace(/_/g, ' ') }}
                        >
                            <div>EMR APPOINTMENT TYPE</div>
                            <div />
                            <div>{labels.calendarHeader}</div>
                            {showConf && <div title="How sure the AI is that this is the right match (0–100%).">CONFIDENCE</div>}
                            <div className="text-right">STATUS / ACTIONS</div>
                        </div>

                        <div
                            className="grid items-center gap-[14px] px-[22px] py-[10px] border-b border-mapper-border-3 bg-mapper-white"
                            style={{ gridTemplateColumns: aiGridCols.replace(/_/g, ' ') }}
                        >
                            <div>
                                <Dropdown compact allLabel="All types" value={typeNameFilter} options={filterOptions.type_name.map(v => ({ value: v, label: v }))} onChange={setTypeNameFilter} />
                            </div>
                            <div />
                            <div>
                                <Dropdown compact allLabel={labels.isAvEmr ? "All services" : "All calendars"} value={calendarNameFilter} options={filterOptions.calendar_name.map(v => ({ value: v, label: v }))} onChange={setCalendarNameFilter} />
                            </div>
                            {showConf && (
                                <div>
                                    <Dropdown compact allLabel="All confidence" value={confidenceTier} options={CONFIDENCE_TIER_OPTIONS} onChange={setConfidenceTier} />
                                </div>
                            )}
                            <div className="flex justify-end items-center gap-3">
                                {(statusFilter || confidenceTier || typeNameFilter || calendarNameFilter || query) && (
                                    <button onClick={handleClearFilters} className="text-[12px] font-semibold text-mapper-teal whitespace-nowrap">
                                        Clear filters
                                    </button>
                                )}
                                <Dropdown compact allLabel="All statuses" value={statusFilter} options={STATUS_OPTIONS} onChange={setStatusFilter} />
                            </div>
                        </div>

                        {loadingSuggestions ? (
                            <div className="px-[22px] py-8 text-[13.5px] text-mapper-muted text-center">Loading…</div>
                        ) : suggestions.length === 0 ? (
                            <div className="px-[22px] py-8 text-[13.5px] text-mapper-muted text-center">No appointment type mappings found.</div>
                        ) : (
                            suggestions.map(r => {
                                if (r.is_external || r.status === 'engine_mapped') {
                                    return (
                                        <div
                                            key={r.id}
                                            title={r.reasoning}
                                            className="grid items-center gap-[14px] px-[22px] py-[15px] border-b border-mapper-border-4 last:border-b-0 opacity-70 hover:opacity-100 transition-opacity cursor-help"
                                            style={{ gridTemplateColumns: aiGridCols.replace(/_/g, ' ') }}
                                        >
                                            <div className="min-w-0">
                                                <div className="text-[13.5px] font-bold truncate">{r.type_name}</div>
                                                {hasSubtype(r.subtype_name) && <div className="text-xs text-mapper-muted-2 truncate">{r.subtype_name}</div>}
                                                <div className="text-[10.5px] text-mapper-muted-5 font-mono mt-[2px]">{r.type_external_id}</div>
                                            </div>
                                            <MappingCoverageArrow coverage={r.mapping_coverage} />
                                            <div className="min-w-0">
                                                <div className="text-[13.5px] font-semibold truncate">{r.suggested_calendar_name}</div>
                                                <div className="text-[10.5px] text-mapper-muted-5 font-mono mt-[2px] truncate">{r.suggested_calendar_external_id}</div>
                                            </div>
                                            {showConf && <div />}
                                            <div className="flex flex-col items-end gap-[7px]">
                                                <MappingCoverageBadge coverage={r.mapping_coverage} />
                                            </div>
                                        </div>
                                    );
                                }
                                const actionable = r.status === 'pending';
                                return (
                                    <div
                                        key={r.id}
                                        className="grid items-center gap-[14px] px-[22px] py-[15px] border-b border-mapper-border-4 last:border-b-0"
                                        style={{ gridTemplateColumns: aiGridCols.replace(/_/g, ' ') }}
                                    >
                                        <div className="min-w-0">
                                            <div className="text-[13.5px] font-bold truncate">{r.type_name}</div>
                                            {hasSubtype(r.subtype_name) && <div className="text-xs text-mapper-muted-2 truncate">{r.subtype_name}</div>}
                                            <div className="text-[10.5px] text-mapper-muted-5 font-mono mt-[2px]">{r.type_external_id}</div>
                                        </div>
                                        <div className="flex justify-center text-mapper-muted-6">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M5 12h14M13 6l6 6-6 6" />
                                            </svg>
                                        </div>
                                        <div className="min-w-0">
                                            {r.suggested_calendar_id ? (
                                                <>
                                                    <div className="text-[13.5px] font-semibold truncate">{r.suggested_calendar_name}</div>
                                                    <div className="text-[10.5px] text-mapper-muted-5 font-mono mt-[2px] truncate">{r.suggested_calendar_external_id}</div>
                                                </>
                                            ) : (
                                                <span className="inline-flex items-center gap-[6px] text-[12.5px] font-semibold text-mapper-red bg-mapper-red-bg px-[10px] py-1 rounded-lg">
                                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                                                        <path d="M12 8v5M12 16v.5" /><circle cx="12" cy="12" r="9" />
                                                    </svg>
                                                    No match — map manually
                                                </span>
                                            )}
                                        </div>
                                        {showConf && (
                                            <div>
                                                <button
                                                    type="button"
                                                    onClick={() => setReasoningModal({ src: typeLabel(r.type_name, r.subtype_name), tgt: r.suggested_calendar_name || 'No match', reasoning: r.reasoning })}
                                                    title={r.reasoning || 'No explanation provided yet.'}
                                                    className="block w-full text-left cursor-pointer"
                                                >
                                                    <ConfidenceBar score={r.confidence_score} />
                                                </button>
                                            </div>
                                        )}
                                        <div className="flex flex-col items-end gap-[7px]">
                                            <StatusBadge status={r.status as 'pending' | 'approved' | 'rejected'} />
                                            <ReviewedByCaption reviewedByName={r.reviewed_by_name} reviewedAt={r.reviewed_at} />
                                            {confirmDeleteId === r.id ? (
                                                <div className="flex items-center gap-[6px]">
                                                    <span className="text-[11.5px] font-semibold text-mapper-red">Delete?</span>
                                                    <button onClick={() => handleDelete(r.id)} disabled={deleting} className="px-2 py-1 rounded-md bg-mapper-red text-white text-[11px] font-semibold disabled:opacity-60">Yes</button>
                                                    <button onClick={() => setConfirmDeleteId(null)} disabled={deleting} className="px-2 py-1 rounded-md border border-mapper-border-2 text-mapper-muted text-[11px] font-semibold">No</button>
                                                </div>
                                            ) : (
                                                <div className="flex gap-[6px]">
                                                    {actionable && (
                                                        <>
                                                            <button onClick={() => handleApprove(r.id)} title="Approve" className="w-[30px] h-[30px] rounded-lg border border-mapper-teal-border bg-mapper-teal-bg text-mapper-teal-hover flex items-center justify-center">
                                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                                                            </button>
                                                            <button onClick={() => handleReject(r.id)} title="Reject" className="w-[30px] h-[30px] rounded-lg border border-mapper-red-border bg-mapper-red-bg-2 text-mapper-red flex items-center justify-center">
                                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                                                            </button>
                                                        </>
                                                    )}
                                                    <button onClick={() => openEdit(r)} title="Edit / remap" className="w-[30px] h-[30px] rounded-lg border border-mapper-border-2 bg-mapper-white text-mapper-muted flex items-center justify-center">
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M11 4H4v16h16v-7" /><path d="M18.5 2.5a2.1 2.1 0 0 1 3 3L12 15l-4 1 1-4z" />
                                                        </svg>
                                                    </button>
                                                    <button onClick={() => setConfirmDeleteId(r.id)} title="Delete" className="w-[30px] h-[30px] rounded-lg border border-mapper-red-border bg-mapper-white text-mapper-red flex items-center justify-center">
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M10 11v6M14 11v6" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                        <Pagination page={page} totalPages={Math.max(1, Math.ceil(total / PAGE_SIZE))} onPageChange={setPage} />
                    </div>
                </div>
            ) : (
                <ManualPicker
                    srcLabel="EMR APPOINTMENT TYPE"
                    tgtLabel={labels.calendarHeader}
                    srcItems={srcItems}
                    tgtItems={tgtItems}
                    loading={loadingManualData}
                    onMap={handleManualMap}
                    onError={(message) => setToast({ message, type: 'error' })}
                    history={history}
                    extraFieldsPanel={emrSource === 'cobalt' ? (
                        <div className="bg-mapper-purple-bg-2 border border-mapper-purple-border rounded-[14px] px-[22px] py-[18px] mb-[18px]">
                            <div className="flex items-center gap-[9px] mb-1">
                                <span className="px-[9px] py-[2px] rounded-md bg-mapper-purple-bg text-mapper-purple text-[11px] font-bold">Cobalt</span>
                                <h3 className="text-sm font-bold m-0">Extra EHR fields</h3>
                            </div>
                            <p className="text-[12.5px] text-mapper-muted-2 m-0 mb-[14px]">
                                Cobalt requires these IDs on every appointment mapping. They're stored with each mapping you create below.
                            </p>
                            <div className="grid grid-cols-2 gap-4 max-w-[560px]">
                                <div>
                                    <label className="block text-xs font-semibold text-mapper-body-text-2 mb-[5px]">Resource EHR ID</label>
                                    <input
                                        value={resourceEhrId}
                                        onChange={e => setResourceEhrId(e.target.value)}
                                        placeholder="e.g. RES-1204"
                                        className="w-full px-3 py-[9px] border border-mapper-border-2 rounded-[9px] text-[13px] font-mono outline-none bg-mapper-white"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-mapper-body-text-2 mb-[5px]">Provider EHR ID</label>
                                    <input
                                        value={providerEhrId}
                                        onChange={e => setProviderEhrId(e.target.value)}
                                        placeholder="e.g. PROV-0456"
                                        className="w-full px-3 py-[9px] border border-mapper-border-2 rounded-[9px] text-[13px] font-mono outline-none bg-mapper-white"
                                    />
                                </div>
                            </div>
                        </div>
                    ) : undefined}
                />
            )}

            <ReasoningModal
                open={!!reasoningModal}
                onClose={() => setReasoningModal(null)}
                srcLabel={reasoningModal?.src ?? ''}
                tgtLabel={reasoningModal?.tgt ?? ''}
                reasoning={reasoningModal?.reasoning ?? ''}
            />

            <EditMappingModal
                open={!!editSuggestion}
                onClose={() => setEditSuggestion(null)}
                onMap={handleEditMap}
                onError={(message) => setToast({ message, type: 'error' })}
                tgtItems={editCalendars.map(c => ({ id: c.id, name: c.name, sub: c.description || c.calendar_type }))}
                tgtLabel={labels.calendarHeader}
                srcLabel="APPOINTMENT TYPE"
                loading={loadingEditCalendars}
                lockedSrc={editSuggestion ? { name: typeLabel(editSuggestion.type_name, editSuggestion.subtype_name) } : { name: '' }}
            />

            <ReverseMappingConflictDialog
                open={!!conflict}
                calendarName={conflict?.calendarName ?? ''}
                conflictingTypeName={conflict?.conflictingTypeName ?? ''}
                newTypeName={conflict?.newTypeName ?? ''}
                busy={resolvingConflict}
                onCancel={() => setConflict(null)}
                onKeep={async () => {
                    if (!conflict) return;
                    setResolvingConflict(true);
                    try {
                        await conflict.retry('keep');
                        setConflict(null);
                    } catch (err) {
                        setToast({ message: err instanceof Error ? err.message : 'Failed to complete mapping', type: 'error' });
                    } finally {
                        setResolvingConflict(false);
                    }
                }}
                onOverwrite={async () => {
                    if (!conflict) return;
                    setResolvingConflict(true);
                    try {
                        await conflict.retry('overwrite');
                        setConflict(null);
                    } catch (err) {
                        setToast({ message: err instanceof Error ? err.message : 'Failed to complete mapping', type: 'error' });
                    } finally {
                        setResolvingConflict(false);
                    }
                }}
            />
        </div>
    );
};
